import React from 'react';
import { Users, Zap, Shield, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const EventParkingManagement: React.FC = () => {
  useDynamicSEO({
    title: "Event & Exhibition Management | Highcrowd Parking Automation",
    description: "Manage massive visitor flows with intelligent event & exhibition parking management software. Handle peak rush smoothly with automated entry & exit monitoring.",
        keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
    ogTitle: "Event & Exhibition Management | Highcrowd Parking Automation",
    ogDescription: "Manage massive visitor flows with intelligent event & exhibition parking management software. Handle peak rush smoothly with automated entry & exit monitoring."
  });

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive's Event & Exhibition Parking Management system. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const features = [
    {
      icon: <Zap className="h-6 w-6 text-blue-600" />,
      title: "Create event-specific parking zones within minutes",
      description: "Quick setup for any event size or duration"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-green-600" />,
      title: "Configure pricing for different vehicle types and durations",
      description: "Flexible pricing rules for cars, bikes, and special vehicles"
    },
    {
      icon: <Clock className="h-6 w-6 text-purple-600" />,
      title: "Rapid mobile-based scanning for entry and exit",
      description: "Instant number plate recognition through mobile app"
    },
    {
      icon: <Shield className="h-6 w-6 text-orange-600" />,
      title: "Digital payments for faster turnover and zero leakages",
      description: "Secure, transparent financial control with automated records"
    },
    {
      icon: <Users className="h-6 w-6 text-indigo-600" />,
      title: "Temporary staff / team permissions with secure access",
      description: "Role-based access for volunteers and temporary staff"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      title: "Real-time vehicle logs and live collection tracking",
      description: "Monitor operations and revenue in real-time"
    }
  ];

  const venues = [
    "Exhibition grounds and trade expos",
    "Stadiums and sports events",
    "Convention centers and corporate summits",
    "Concert halls and public festivals",
    "Tourism fairs and cultural celebrations",
    "College and university events",
    "Political and public gatherings",
    "Temporary outdoor parking zones"
  ];

  const faqs = [
    {
      question: "Can Park Hive be used for temporary parking during short-term events?",
      answer: "Yes — setup takes minutes and works perfectly for temporary installations."
    },
    {
      question: "Do we need expensive hardware?",
      answer: "No — operations run directly through the mobile app."
    },
    {
      question: "Can the system handle extremely high crowd volumes?",
      answer: "Yes — it is built to withstand rapid check-in/out surges."
    },
    {
      question: "Can staff or volunteers operate the app easily?",
      answer: "Absolutely — training takes only a few minutes."
    },
    {
      question: "What about reporting and settlement?",
      answer: "All collection and vehicle records are automatically stored and can be exported instantly."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Event & Exhibition <span className="text-blue-600">Parking Management</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-8">
              Smart Parking Management for Events, Exhibitions, Festivals & Large Crowd Gatherings
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Events are supposed to be memorable. Exhibitions should feel grand, organized, and seamless. 
              But nothing destroys that experience faster than the chaos at the parking gate — the endless queues, 
              frustrated drivers, honking, arguments, confused staff, lost tickets, incorrect charges, and 
              panicked organizers trying to hold everything together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Book a Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="border border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-12">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-8 w-8 text-red-600 mr-3" />
                <h3 className="text-2xl font-bold text-red-900">The Parking Crisis at Events</h3>
              </div>
              <p className="text-red-800 text-lg leading-relaxed">
                For parking lot owners and event managers, the pressure is brutal during live events. 
                Thousands arrive at the same time. Every minute counts. Every decision matters. 
                And a single choke point at entry or exit can ruin the entire event experience.
              </p>
            </div>

            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Parking Defines the Success of Events
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-green-900 mb-2">First Impression</h4>
                  <p className="text-green-700">If entry feels smooth, they walk in relaxed.</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-900 mb-2">Experience Management</h4>
                  <p className="text-blue-700">Event organizers carry the weight of experience.</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h4 className="font-bold text-purple-900 mb-2">Last Memory</h4>
                  <p className="text-purple-700">If exit is effortless, they leave with a smile.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Built for High Volume. Designed for Real Pressure.
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Events and exhibitions are unpredictable in volume and behavior. Crowds don't trickle — they surge. 
              Traffic peaks in sudden waves. Traditional manual ticketing collapses under such intensity.
            </p>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-blue-600 mb-4">Park Hive's Event Solution</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                Using mobile-based instant scanning, automated charge calculation, and real-time reporting, 
                operators manage unpredictable traffic with the confidence of technology instead of panic.
              </p>
              <div className="mt-6 flex justify-center space-x-8 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">⚡</div>
                  <p className="text-sm text-gray-600">Fast check-in</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">🚀</div>
                  <p className="text-sm text-gray-600">Fast check-out</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">✨</div>
                  <p className="text-sm text-gray-600">Zero confusion</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How It Works During Live Events
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <h4 className="text-xl font-bold text-blue-900 ml-3">Instant Check-In</h4>
                </div>
                <p className="text-blue-800">
                  Vehicles arrive and are checked in instantly using number scan recognition through the Park Hive mobile app. 
                  No manual writing. No delays. The stay duration begins tracking automatically.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <h4 className="text-xl font-bold text-green-900 ml-3">Instant Check-Out</h4>
                </div>
                <p className="text-green-800">
                  When the event ends and thousands rush out at once, the exit scan calculates charges instantly 
                  based on the event's pricing rules. Digital payment completes the cycle instantly.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-8 text-center">
              <h4 className="text-xl font-bold text-purple-900 mb-3">Real-Time Control</h4>
              <p className="text-purple-800 text-lg">
                Organizers watch revenue, occupancy, and operator performance live — right from the dashboard. 
                No shouting. No waiting. No breakdown. Only flow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Features That Event Parking Depends On
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Park Hive gives event parking operators everything they need to stay in control even during chaotic surges:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                  <div className="flex items-start mb-3">
                    <div className="mr-4 mt-1">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg font-medium text-gray-700">
                Every feature exists for one purpose: <span className="text-blue-600 font-bold">movement without disruption.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Venues Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              Where This Matters the Most
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              This solution is ideal for venues handling sudden, large-volume vehicle traffic such as:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {venues.map((venue, index) => (
                <div key={index} className="flex items-center bg-blue-50 rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                  <span className="text-blue-900 font-medium">{venue}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Wherever crowds gather, speed is survival.</h3>
              <p className="text-xl">Park Hive keeps that speed alive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Control Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
                Serious Revenue Control. Zero Leakage.
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-red-600 mb-3">❌ Manual Problems</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Cash-based systems prone to loss</li>
                    <li>• Manual slip systems create errors</li>
                    <li>• Untraceable revenue handling</li>
                    <li>• Disputes and confusion</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-green-600 mb-3">✅ Digital Solutions</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Digital check-in/out records</li>
                    <li>• Automated financial tracking</li>
                    <li>• Every rupee accounted for</li>
                    <li>• Clean, transparent control</li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center bg-green-50 rounded-lg p-6">
                <p className="text-lg text-green-800">
                  <strong>Organizers see exactly what is collected, when, and by whom.</strong><br />
                  No disputes. No confusion. No hidden transactions. Just clean, transparent financial control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              For Owners Who Want Reputation, Not Just Parking Space
            </h2>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Successful event parking isn't about filling slots. It's about moving people efficiently and leaving a lasting impression.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Technology vs Chaos</h4>
                  <p className="text-sm text-gray-600">The difference between chaos and mastery</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Complaints vs Compliments</h4>
                  <p className="text-sm text-gray-600">Transform visitor experience</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Surviving vs Owning</h4>
                  <p className="text-sm text-gray-600">Master your event operations</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-600 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-4">Park Hive gives parking operators the confidence to say:</h3>
              <p className="text-xl italic">"No matter the crowd size, we've got it under control."</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Request a Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200"
              >
                Book a Demo
              </button>
            </div>
            
            <p className="text-gray-600 mt-6">
              Experience the difference between manual chaos and automated flow.<br />
              See the live workflow, test the tools, and witness how stress vanishes when technology takes over.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Note */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Final Note</h2>
            
            <div className="space-y-6 text-lg leading-relaxed">
              <p>An event's success doesn't start on stage —<br />
              <strong className="text-blue-300">it begins at the parking gate.</strong></p>
              
              <p>When movement is smooth, experience becomes unforgettable.</p>
              
              <p><strong className="text-blue-300">Park Hive makes that possible.</strong></p>
              
              <p>Because crowd management isn't a challenge —<br />
              <strong className="text-green-300">It's an engineering opportunity.</strong></p>
            </div>
            
            <div className="mt-12">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                </svg>
                Let's make your next event the smoothest one ever
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventParkingManagement;